<script setup>
const props = defineProps({
  title: {
    type: String,
    required: true
  },
  blurb: {
    type: String,
    required: true
  },
  action: {
    type: Function,
    required: false
  },
  actionText: {
    type: String,
    default : "Create new"
  },
  icon: {
    type: String,
    default: 'mdi-alert'
  },
  iconColor: {
    type: String,
    default: 'primary'
  },
  size: {
    type: String,
    default: 'large',
    validator: value => ['small', 'medium', 'large'].includes(value)
  }
});

const buttonSize = computed(() => {
  switch (props.size) {
    case 'small':
      return 'x-small';
    case 'medium':
      return 'small';
    case 'large':
      return 'large';
  }
});

const iconSize = computed(() => {
  switch (props.size) {
    case 'small':
      return 22;
    case 'medium':
      return 28;
    case 'large':
      return 36;
  }
});

</script>

<template>
  <div class="no-existing bg-white boxed  d-flex" :class="`size-${size}`">
    <div class="icon-container">
      <v-icon :icon="icon" :color="iconColor" :size="iconSize"></v-icon>
    </div>
    <div class="content">
      <h2 class="title">{{ title }}</h2>
      <div class="blurb" v-if="blurb">
        {{ blurb }}
      </div>
      <!-- Hosts that need more than one action, or a link rather than a
           handler, supply their own buttons here. -->
      <div v-if="$slots.actions" class="actions">
        <slot name="actions"/>
      </div>
      <div v-else-if="action">
        <v-btn :size="buttonSize" @click="action" color="primary">{{ actionText }}</v-btn>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* Empty state — centered column: icon tile, title, blurb, action */
.no-existing {
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.icon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: rgb(var(--v-theme-light));
  margin-bottom: 14px;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;

  .title {
    font-size: 14.5px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 1.4;
    margin-bottom: 0;
  }

  .blurb {
    font-size: 12px;
    color: rgb(var(--v-theme-textSubtle));
    line-height: 1.6;
    margin-top: 6px;
    max-width: 380px;
  }

  .v-btn {
    margin-top: 14px;
  }

  /* Slotted buttons carry the host's scope id, so the .v-btn margin above never
     reaches them and they sat flush under the blurb. Space the wrapper instead. */
  .actions {
    margin-top: 14px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
  }
}

/* Size variants scale padding and the icon tile only */
.size-small {
  padding: 20px 14px;

  .icon-container {
    width: 44px;
    height: 44px;
    border-radius: 14px;
    margin-bottom: 10px;
  }

  .content .title {
    font-size: 13.5px;
  }

  .content .blurb {
    font-size: 11.5px;
  }
}

.size-medium {
  padding: 32px 20px;

  .icon-container {
    width: 54px;
    height: 54px;
    border-radius: 17px;
  }
}

.size-large {
  padding: 48px 28px;
}
</style>
